import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  CircularProgress,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ConfirmDialog, GoogleMapsProvider, SplitFormShell } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useBulletinSource } from "./hooks/useBulletinSource";
import { FormActionBar, FormActions } from "./layout/FormActionBar";
import { FormSection } from "./layout/FormSection";
import { useSectionAnchors } from "./layout/sectionAnchors";
import { SectionRail, SectionRailItem } from "./layout/SectionRail";
import { SectionStepper } from "./layout/SectionStepper";
import { withSavableEntities } from "./model/entityRules";
import { createBulletinRepository } from "./persistence/bulletinRepository";
import { useBulletinRow } from "./persistence/bulletinRowContext";
import { useAutosave } from "./persistence/useAutosave";
import {
  BulletinPreview,
  PreviewView,
  PreviewViewTabs,
} from "./preview/BulletinPreview";
import { ContactsSection } from "./sections/ContactsSection";
import { EntitiesSection } from "./sections/EntitiesSection";
import { IncidentSection } from "./sections/IncidentSection";
import { MediaSection } from "./sections/MediaSection";
import { NarrativeSection } from "./sections/NarrativeSection";
import { BULLETIN_SECTION_IDS, BulletinSectionId } from "./sections/sectionIds";
import { SetupSection } from "./sections/SetupSection";
import { useBulletinValues } from "./state/bulletinFormContext";
import { BulletinFormProvider } from "./state/BulletinFormProvider";
import { FinalizeAttemptProvider } from "./validation/finalizeAttempt";
import {
  collectIssues,
  computeSectionCompletion,
} from "./validation/sectionErrors";
import { useBulletinSchema } from "./validation/useBulletinSchema";

const SECTION_CONTENT: Record<BulletinSectionId, React.ReactNode> = {
  setup: <SetupSection />,
  incident: <IncidentSection />,
  entities: <EntitiesSection />,
  media: <MediaSection />,
  narrative: <NarrativeSection />,
  contact: <ContactsSection />,
};

type ExpandedState = Record<BulletinSectionId, boolean>;

const initialExpanded = (): ExpandedState =>
  BULLETIN_SECTION_IDS.reduce(
    (state, id, index) => ({ ...state, [id]: index === 0 }),
    {} as ExpandedState,
  );

/**
 * The one-page bulletin form.
 *
 * There is no step gating: any section can be opened at any time. Completing one opens the next,
 * and completed sections stay open — reviewers asked for the second part explicitly.
 */
interface BulletinFormProps {
  /**
   * Whether the bulletin this form is editing is already live. It changes three things and nothing
   * else: what `save` puts on the wire, what the footer offers, and where leaving goes.
   */
  published: boolean;
}

const BulletinForm: React.FC<BulletinFormProps> = ({ published }) => {
  const { t } = useTranslation("bulletin/create-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  // The one place that deliberately subscribes to the whole document: the rail ticks, the progress
  // label and the outstanding count are all derived from it, so this component re-renders on every
  // keystroke. The sections do not follow it down — `FormSection` is memoised and its `children` are
  // the module-level elements above, so a keystroke re-renders this shell and the one section that
  // owns the field.
  const values = useBulletinValues();
  const { trigger } = useFormContext();
  const { sectionRefs, scrollToSection } = useSectionAnchors();

  // Autosave lives here because this component already subscribes to the whole document; putting it
  // in a section would make that section follow every keystroke on the page.
  //
  // The repository is built from the row holder rather than imported, so it PATCHes the same row
  // the media uploads created — one form instance, one bulletin (decision 21).
  //
  // `withSavableEntities` drops the blank row that clicking "Add person, vehicle, or item" puts in
  // the form. Without it, blur beats click and Add-then-Done saves the blank before removing it.
  const row = useBulletinRow();
  const repository = useMemo(
    () => createBulletinRepository(row, published),
    [row, published],
  );
  /**
   * Off while publishing, and back on if publishing fails.
   *
   * `finalize` is one-way — the endpoint refuses to put a published bulletin back into draft — so a
   * timer that fires after it resolves would send `is_draft: true` and be rejected. Navigating away
   * makes that unlikely rather than impossible, which is not the same thing.
   */
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);
  const autosave = useAutosave(
    repository,
    withSavableEntities,
    autosaveEnabled,
  );

  // The actions sit in the rail on lg and in the fixed bottom bar below it — never in both.
  const isWide = useMediaQuery((theme as Theme).breakpoints.up("lg"));

  const [expanded, setExpanded] = useState<ExpandedState>(initialExpanded);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewView, setPreviewView] = useState<PreviewView>("full");
  /** True while Finalize or Discard is in flight, so neither can be fired twice. */
  const [busy, setBusy] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  /**
   * The rail highlight follows intent, not scroll position: whichever section you last jumped to
   * or put the caret in. Scroll position was tried and does not work here — the reading band a
   * scroll-spy needs sits near the top of the viewport, so once the page is at the bottom the
   * short trailing sections can never enter it and simply never highlight.
   */
  const [activeId, setActiveId] = useState<BulletinSectionId | null>(
    BULLETIN_SECTION_IDS[0],
  );

  /**
   * Everything still standing between this draft and Finalize, from the same schema the resolver
   * runs — see `validation/sectionErrors.ts` for why it is re-run here rather than read off
   * `formState.errors`.
   */
  const schema = useBulletinSchema(values.bulletinType);
  const issues = useMemo(() => collectIssues(values, schema), [values, schema]);

  const completion = useMemo(
    () => computeSectionCompletion(values, issues),
    [values, issues],
  );

  /**
   * Set by the first blocked Finalize and never cleared. It only reaches the row lists, whose
   * inputs react-hook-form does not register — see `validation/finalizeAttempt.ts`.
   */
  const [finalizeAttempted, setFinalizeAttempted] = useState(false);

  // Completing a section opens the next one, once. A section the user has since closed by hand
  // stays closed, which is why this only ever sets `true` on the transition.
  const previousCompletion = useRef(completion);
  useEffect(() => {
    const justCompleted = BULLETIN_SECTION_IDS.filter(
      (id) => completion[id] && !previousCompletion.current[id],
    );
    previousCompletion.current = completion;
    if (justCompleted.length === 0) return;

    setExpanded((state) => {
      const next = { ...state };
      justCompleted.forEach((id) => {
        const following =
          BULLETIN_SECTION_IDS[BULLETIN_SECTION_IDS.indexOf(id) + 1];
        if (following) next[following] = true;
      });
      return next;
    });
  }, [completion]);

  const toggleSection = useCallback(
    (id: BulletinSectionId, open: boolean) =>
      setExpanded((state) => ({ ...state, [id]: open })),
    [],
  );

  const jumpTo = useCallback(
    (id: BulletinSectionId) => {
      // Going to a section means going to the form. Below lg the preview is a sheet over it, and
      // the outstanding-field count in the bar is reachable from under that sheet — without this
      // it would open and scroll a section nobody can see.
      setPreviewOpen(false);
      setExpanded((state) => ({ ...state, [id]: true }));
      setActiveId(id);
      scrollToSection(id);
    },
    [scrollToSection],
  );

  /**
   * What both the count and the disabled Finalize do: say what is missing, everywhere at once, and
   * go to the first of it.
   *
   * `trigger()` is the half that reaches the registered fields — until it runs, react-hook-form has
   * only ever validated fields the user touched, so a form nobody has typed into holds no errors to
   * show. The flag is the half that reaches the row lists.
   */
  const jumpToFirstIssue = useCallback(() => {
    setFinalizeAttempted(true);
    void trigger();

    const first = issues[0];
    if (first) jumpTo(first.section);
  }, [issues, jumpTo, trigger]);

  const railItems: SectionRailItem[] = BULLETIN_SECTION_IDS.map((id) => ({
    id,
    label: t(`sections.${id}`),
    complete: completion[id],
  }));

  const completeCount = railItems.filter((item) => item.complete).length;
  const progressLabel = t("rail.progress", {
    done: completeCount,
    total: railItems.length,
  });

  /**
   * Publish. The order is the point: stop autosave, publish, leave.
   *
   * On failure the bulletin is still a draft, so autosave has to come back — a form that silently
   * stopped saving would be worse than the race this avoids.
   */
  const finalize = async () => {
    setBusy(true);
    setAutosaveEnabled(false);
    try {
      const saved = await repository.finalize(withSavableEntities(values));
      toast.success(t("actions.finalized"));
      // The published bulletin, pinned open with its access panel showing. `pinToFront` is what
      // makes it the main card that stays put: without it `AlertsList` treats a click on another
      // bulletin as navigation instead of opening it beside this one, which is the whole point of
      // arriving with `similarTo`.
      navigate(
        `/rubialert/alerts?bulletinId=${saved.id}&similarTo=${saved.id}` +
          `&pinToFront=true&access=true`,
      );
    } catch {
      toast.error(t("actions.finalize_failed"));
      setAutosaveEnabled(true);
      setBusy(false);
    }
  };

  const discard = async () => {
    setDiscardOpen(false);
    setBusy(true);
    setAutosaveEnabled(false);
    try {
      await repository.discard();
      // The drafts list rather than the start screen: the draft is gone, and its siblings are what
      // the officer came from in edit mode and will want to see in either.
      navigate("/rubialert/drafts");
    } catch {
      toast.error(t("actions.discard_failed"));
      setAutosaveEnabled(true);
      setBusy(false);
    }
  };

  /**
   * A live bulletin has nothing to finalize and nothing to discard — every edit is already public
   * the moment autosave sends it — so all that is left is leaving. See `FormActions`.
   */
  const formActions: FormActions = published
    ? {
        kind: "published",
        onBack: () => navigate(`/rubialert/alerts/${row.id()}/single`),
      }
    : {
        kind: "draft",
        onFinalize: finalize,
        onDiscard: () => setDiscardOpen(true),
        busy,
      };

  const footer = (
    <FormActionBar
      requiredLeft={issues.length}
      onJumpToFirstIssue={jumpToFirstIssue}
      save={autosave}
      actions={formActions}
    />
  );

  return (
    <>
      <SplitFormShell
        previewOpen={previewOpen}
        onPreviewOpenChange={setPreviewOpen}
        previewLabel={t("rail.preview")}
        hidePreviewLabel={t("actions.edit")}
        previewToolbar={
          <PreviewViewTabs view={previewView} onViewChange={setPreviewView} />
        }
        rail={
          <SectionRail
            items={railItems}
            activeId={activeId}
            onJump={jumpTo}
            eyebrow={t("rail.eyebrow")}
            progressLabel={progressLabel}
            footer={isWide ? footer : undefined}
          />
        }
        stepper={
          <SectionStepper
            items={railItems}
            activeId={activeId}
            onJump={jumpTo}
            progressLabel={progressLabel}
          />
        }
        form={
          // One handler for the whole column: React's blur bubbles, so this is the "field blur"
          // autosave trigger for every field on the page without any of them knowing about it.
          <FinalizeAttemptProvider value={finalizeAttempted}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              onBlurCapture={autosave.onBlurCapture}
            >
              {BULLETIN_SECTION_IDS.map((id) => (
                <FormSection
                  key={id}
                  ref={sectionRefs[id]}
                  id={id}
                  title={t(`sections.${id}`)}
                  description={t(`sections.${id}_description`)}
                  complete={completion[id]}
                  expanded={expanded[id]}
                  onToggle={toggleSection}
                  onActivate={setActiveId}
                >
                  {SECTION_CONTENT[id]}
                </FormSection>
              ))}
            </Box>
          </FinalizeAttemptProvider>
        }
        preview={<BulletinPreview view={previewView} />}
        // Below lg the preview is a sheet, so the bar carries the control that opens and closes
        // it. The rail's copy of the footer gets no toggle: there the preview is a column.
        actions={React.cloneElement(footer, {
          orientation: "row",
          preview: {
            open: previewOpen,
            onToggle: () => setPreviewOpen((open) => !open),
          },
        })}
      />

      {/*
        Deleting the row is irreversible and takes the media with it, so it is the one action on
        this page that asks first. The emphasised answer is the safe one — `ConfirmDialog`'s own
        rule, and what a hurried tap should land on. Unreachable on a live bulletin, which is not
        deleted from an edit screen.
      */}
      <ConfirmDialog
        open={discardOpen}
        title={t("discard.title")}
        message={t("discard.message")}
        primaryLabel={t("discard.keep")}
        onPrimary={() => setDiscardOpen(false)}
        secondaryLabel={t("discard.confirm")}
        onSecondary={discard}
      />
    </>
  );
};

/** The three states before the form exists. Deliberately plain — none of them is a place to linger. */
const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1.5,
      py: 8,
    }}
  >
    {children}
  </Box>
);

/**
 * Create and edit are the same page.
 *
 * **There is no `mode` prop.** The route is the mode: `/rubialert/bulletins/new/form` has no
 * `:bulletinId` and `/rubialert/bulletins/:bulletinId/edit` does, so a prop could only repeat the
 * URL and eventually contradict it. Everything else about the difference lives in
 * `hooks/useBulletinSource.ts`.
 *
 * The form is not mounted until the values exist. That is a safety property, not a loading state —
 * see the hook for why.
 */
const BulletinFormPageContent: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const { bulletinId } = useParams();
  const source = useBulletinSource(
    bulletinId === undefined ? undefined : Number(bulletinId),
  );

  if (source.status === "loading") {
    return (
      <Notice>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {t("edit.loading")}
        </Typography>
      </Notice>
    );
  }

  if (source.status === "error") {
    return (
      <Notice>
        <Typography variant="body1">{t("edit.load_failed")}</Typography>
      </Notice>
    );
  }

  return (
    <BulletinFormProvider
      // A different bulletin is a different form: `defaultValues` is captured once, so without this
      // navigating from one draft to another would keep the first one's values.
      key={source.bulletinId ?? "new"}
      initialValues={source.values ?? undefined}
      bulletinId={source.bulletinId ?? undefined}
    >
      <BulletinForm published={!source.isDraft} />
    </BulletinFormProvider>
  );
};

/**
 * The Maps API is mounted here for the same reason `UpdateAlert` mounts it: the two places that need
 * it read the global `google` rather than a hook — `GooglePlacesAutocomplete` in `LocationRow`, and
 * the `Geocoder` in `geocodePlace`. Without this the autocomplete never initialises its service and
 * the field sits at "No locations" with no request ever made.
 */
export const BulletinFormPage: React.FC = () => (
  <GoogleMapsProvider>
    <BulletinFormPageContent />
  </GoogleMapsProvider>
);
